import {Divider, Loader} from '@mantine/core';
import Image from 'next/image';
import type React from 'react';
import {isReleasedRedesignRecruitment} from '../../../../config/unReleasePage';
import type {ColumnListItemType} from '../../../../core/entities/columns/schema';
import {useGetColumnPostList} from '../../../../core/repositories/columnRepository';
import {CommonLink} from '../../../../shared/components/CommonLink';
import {useMediaQuery} from '../../../../shared/services/useMediaQuery';
import {ColumnClipButton} from '../../../columns/components/Container/ColumnClipButton';
import {useHandleColumnClip} from '../../../columns/usecases/useColumnClip';
import {ColumnCard} from '../../../company/components/ColumnCard';
import {RecruitmentTag} from './Presenter/RecruitmentTag';

type Props = {
  companyId: number;
  companyName: string;
};

export const SpecialPostList: React.FC<Props> = ({companyId, companyName}) => {
  const {isPC} = useMediaQuery();
  const {data, isLoading} = useGetColumnPostList({
    type: 'nativeAd',
    companyID: companyId,
  });

  return isLoading ? (
    <div className="mt-16 flex justify-center items-center">
      <Loader color="gray" />
    </div>
  ) : (
    data?.ok &&
      data.value?.posts.length > 0 &&
      (isReleasedRedesignRecruitment() ? (
        <div className="w-full bg-white p-16 rounded-16 mt-16">
          <h2 className="font-bold">{companyName}の特集記事</h2>
          <Divider className="my-8" />
          <div className="overflow-x-scroll min-w-0">
            <div className="flex flex-nowrap p-8 gap-16 pc:gap-24">
              {data.value?.posts.map((post) => (
                <div key={post.id} className="shrink-0">
                  <ColumnCard post={post} isSponsored={true} />
                </div>
              ))}
            </div>
          </div>
        </div>
      ) : (
        <div className="mt-16">
          <h2 className="text-black-1 text-md font-bold leading bg-background-1 p-16">
            {companyName}の特集記事
          </h2>
          <div className="mt-16">
            {data.value?.posts.map((post, index) => (
              <SpecialPostItem post={post} index={index} isPC={isPC} />
            ))}
          </div>
        </div>
      ))
  );
};

const SpecialPostItem: React.FC<{
  post: ColumnListItemType;
  index: number;
  isPC: boolean;
}> = ({post, index, isPC}) => {
  const columnClip = useHandleColumnClip({
    columnId: post.id,
    clipCount: post.favoriteCount,
    isClipped: post.favorited,
  });

  return (
    <div
      key={`special-post-${index}`}
      className="flex justify-between mt-12 [&:nth-child(1)]:mt-0"
    >
      {post.thumbnailURL && (
        <CommonLink
          href={`/archives/${post.id}`}
          className="w-[103px] h-[103px] pc:hover:opacity-80"
        >
          <Image
            src={post.thumbnailURL}
            alt={post.title}
            className="object-cover"
            width={103}
            height={103}
          />
        </CommonLink>
      )}
      <div className="w-[calc(100%-119px)] flex flex-col justify-center">
        <div className="flex justify-between flex-wrap">
          <ul className="flex flex-wrap w-[calc(100%-61px)]">
            {post.tags.map((tag) => (
              <li key={`special-post-${index}-tag-${tag}`} className="mb-4">
                <RecruitmentTag tag={{id: index, name: tag}} />
              </li>
            ))}
          </ul>
          <div className="w-[53px] ml-8">
            <ColumnClipButton {...columnClip} />
          </div>
        </div>
        <div>
          <CommonLink
            href={`/archives/${post.id}`}
            className="block pc:hover:opacity-80"
          >
            <p>{post.title}</p>
            {isPC && (
              <p className="text-sm text-black-1 leading line-clamp-2">
                {post.contentSummary}
              </p>
            )}
          </CommonLink>
        </div>
      </div>
    </div>
  );
};
